import { cn } from "./classNames-DkX30VJU.js";
import { AlertTriangle, CircleCheck, Info } from "./icons-DDR9dFSr.js";
import { Button } from "./Button-BAMX1iuI.js";
import { createContext, use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { Action, Cancel, Content, Overlay, Portal, Root, Title, Trigger } from "@radix-ui/react-alert-dialog";
// src/components/AlertModal/AlertModalDescription.tsx
const AlertModalDescription = ({ className, ...props }) => /* @__PURE__ */ jsx(AlertDialogPrimitive.Description, {
	className: cn("tw:font-sans", "tw:tracking-normal", "tw:font-normal", "tw:text-muted-foreground", className),
	...props
});
// src/components/AlertModal/AlertModalFooter.tsx
const AlertModalFooter = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	className: cn("tw:flex", "tw:gap-2", "tw:justify-end", className),
	...props
});
// src/components/AlertModal/AlertModalHeader.tsx
const AlertModalHeader = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	className: cn("tw:grid", "tw:grid-cols-[auto_1fr]", "tw:items-start", "tw:gap-y-1", "tw:gap-x-3", className),
	...props
});
// src/components/AlertModal/AlertModalIcon.tsx
const VARIANT_ICONS = {
	info: /* @__PURE__ */ jsx(Info, {}),
	warning: /* @__PURE__ */ jsx(AlertTriangle, {}),
	danger: /* @__PURE__ */ jsx(AlertTriangle, {}),
	success: /* @__PURE__ */ jsx(CircleCheck, {})
};
const AlertModalIcon = ({ variant, icon }) => {
	const glyph = icon ?? VARIANT_ICONS[variant];
	return /* @__PURE__ */ jsx("span", {
		"data-slot": "alert-modal-icon",
		className: cn("tw:inline-flex", "tw:col-start-1", "tw:row-start-1", "tw:self-start", "tw:shrink-0", "tw:items-center", "tw:justify-center", "tw:rounded", "tw:size-8", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-5", {
			"tw:bg-info/10 tw:text-info": variant === "info",
			"tw:bg-warning/10 tw:text-warning": variant === "warning",
			"tw:bg-destructive/10 tw:text-destructive": variant === "danger",
			"tw:bg-success/10 tw:text-success": variant === "success"
		}),
		children: glyph
	});
};
// src/components/AlertModal/AlertModalOverlay.tsx
const AlertModalOverlay = ({ ref, className, ...props }) => /* @__PURE__ */ jsx(Overlay, {
	...props,
	ref,
	className: cn("tw:fixed", "tw:inset-0", "tw:z-overlay", "tw:bg-scrim", "tw:data-[state=open]:duration-500", "tw:data-[state=open]:fade-in-0", "tw:data-[state=open]:animate-in", "tw:data-[state=closed]:duration-500", "tw:data-[state=closed]:fade-out-0", "tw:data-[state=closed]:animate-out", className)
});
// src/components/AlertModal/AlertModalTitle.tsx
const AlertModalTitle = ({ children, className, ...props }) => /* @__PURE__ */ jsx(Title, {
	...props,
	asChild: true,
	className: cn("tw:font-medium", "tw:text-foreground", "tw:wrap-anywhere", className),
	children: /* @__PURE__ */ jsx("h3", { children })
});
// src/components/AlertModal/context.ts
const AlertModalContext = createContext(null);
const AlertModalContextProvider = AlertModalContext.Provider;
const useAlertModalContext = () => {
	const context = use(AlertModalContext);
	if (context === null) throw new Error("AlertModal compound components must be rendered inside <AlertModal>.");
	return context;
};
// src/components/AlertModal/AlertModalContent.tsx
const AlertModalContent = ({ variant = "info", icon, title, cancelLabel = "Cancel", confirmLabel: actionLabel = "Confirm", confirmDisabled = false, container, className, children, onEscapeKeyDown, ...props }) => {
	const { isLoading, onConfirm, onCancel } = useAlertModalContext();
	const handleEscapeKeyDown = (event) => {
		event.stopPropagation();
		if (isLoading) event.preventDefault();
		onEscapeKeyDown?.(event);
	};
	return /* @__PURE__ */ jsx(Portal, {
		container,
		children: /* @__PURE__ */ jsx(AlertModalOverlay, {
			onClick: (event) => event.stopPropagation(),
			onKeyDown: (event) => {
				if (event.key !== "Escape") event.stopPropagation();
			},
			children: /* @__PURE__ */ jsxs(Content, {
				...children ? {} : { "aria-describedby": void 0 },
				onEscapeKeyDown: handleEscapeKeyDown,
				className: cn("tailwindcss-preflight", "tw:text-base", "tw:fixed", "tw:left-1/2", "tw:-translate-x-1/2", "tw:z-overlay", "tw:top-1/2", "tw:-translate-y-1/2", "tw:w-full", "tw:max-w-[480px]", "tw:max-h-[calc(100vh-2*theme(spacing.20))]", "tw:overflow-y-auto", "tw:flex", "tw:flex-col", "tw:gap-4", "tw:rounded-modal", "tw:bg-popover", "tw:text-popover-foreground", "tw:p-4", "tw:duration-200", "tw:data-[state=open]:animate-in", "tw:data-[state=open]:fade-in-0", "tw:data-[state=closed]:animate-out", "tw:data-[state=closed]:fade-out-0", "tw:data-[state=open]:zoom-in-95", "tw:data-[state=closed]:zoom-out-95", className),
				...props,
				children: [/* @__PURE__ */ jsxs(AlertModalHeader, { children: [
					icon !== false && /* @__PURE__ */ jsx(AlertModalIcon, {
						variant,
						icon
					}),
					title && /* @__PURE__ */ jsx(AlertModalTitle, {
						className: cn(icon === false ? "tw:col-start-1" : "tw:col-start-2", "tw:row-start-1 tw:self-center"),
						children: title
					}),
					children && /* @__PURE__ */ jsx(AlertModalDescription, {
						className: cn(icon === false ? "tw:col-start-1" : "tw:col-start-2", title ? "tw:row-start-2" : "tw:row-start-1 tw:self-center"),
						children
					})
				] }), /* @__PURE__ */ jsxs(AlertModalFooter, { children: [/* @__PURE__ */ jsx(Cancel, {
					asChild: true,
					children: /* @__PURE__ */ jsx(Button, {
						type: "button",
						variant: "outline",
						disabled: isLoading,
						onClick: onCancel,
						children: cancelLabel
					})
				}), /* @__PURE__ */ jsx(Action, {
					asChild: true,
					children: /* @__PURE__ */ jsx(Button, {
						type: "button",
						variant: variant === "danger" ? "danger" : "primary",
						isLoading,
						disabled: isLoading || confirmDisabled,
						onClick: onConfirm,
						children: actionLabel
					})
				})] })]
			})
		})
	});
};
// src/components/AlertModal/AlertModalTrigger.tsx
const AlertModalTrigger = ({ ref, onClick, onKeyDown, ...props }) => /* @__PURE__ */ jsx(Trigger, {
	...props,
	ref,
	onClick: (event) => {
		event.stopPropagation();
		onClick?.(event);
	},
	onKeyDown: (event) => {
		if (event.key === "Enter" || event.key === " ") event.stopPropagation();
		onKeyDown?.(event);
	}
});
// src/components/AlertModal/AlertModal.tsx
const noop = () => void 0;
const AlertModal = ({ children, onConfirm = noop, onCancel = noop, skipAsyncWait = false, isLoading = false, open, defaultOpen, onOpenChange, ...rest }) => {
	const [isConfirming, setIsConfirming] = useState(false);
	const isControlled = open !== void 0;
	const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen ?? false);
	const isOpen = isControlled ? open : uncontrolledOpen;
	const confirmRef = useRef(onConfirm);
	const cancelRef = useRef(onCancel);
	const onOpenChangeRef = useRef(onOpenChange);
	useEffect(() => {
		confirmRef.current = onConfirm;
		cancelRef.current = onCancel;
		onOpenChangeRef.current = onOpenChange;
	}, [
		onConfirm,
		onCancel,
		onOpenChange
	]);
	const setOpen = useCallback((next) => {
		if (!isControlled) setUncontrolledOpen(next);
		onOpenChangeRef.current?.(next);
	}, [isControlled]);
	const context = useMemo(() => ({
		isLoading: isConfirming || isLoading,
		onConfirm: (event) => {
			const result = confirmRef.current(event);
			if (!(result !== null && typeof result === "object" && typeof result.then === "function") || skipAsyncWait) return;
			event.preventDefault();
			setIsConfirming(true);
			Promise.resolve(result).then(() => setOpen(false)).finally(() => setIsConfirming(false));
		},
		onCancel: (event) => {
			cancelRef.current(event);
		}
	}), [
		isConfirming,
		isLoading,
		skipAsyncWait,
		setOpen
	]);
	return /* @__PURE__ */ jsx(Root, {
		...rest,
		open: isOpen,
		onOpenChange: setOpen,
		children: /* @__PURE__ */ jsx(AlertModalContextProvider, {
			value: context,
			children
		})
	});
};
AlertModal.Trigger = AlertModalTrigger;
AlertModal.Content = AlertModalContent;
export { AlertModal };

//# sourceMappingURL=AlertModal-BkmyEvsR.js.map